import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IconClose, IconEdit, IconMarkdown, IconMessage } from './Icons';
import './ItemDetailModal.css';

// Build a self-contained Markdown document for a decision / artefact / todo so
// the user can download exactly what they're reading, with its provenance
// (source thread) and authorship baked into the front-matter block.
function buildMarkdown(item) {
  const lines = [`# ${item.title || 'Untitled'}`, ''];
  const meta = [];
  if (item.tagLabel) meta.push(`- **Type:** ${item.tagLabel}`);
  if (item.roomName) meta.push(`- **Thread:** ${item.roomName}`);
  if (item.by) meta.push(`- **By:** ${item.by}`);
  if (item.at) {
    const d = new Date(item.at);
    meta.push(`- **Date:** ${Number.isNaN(d.getTime()) ? item.at : d.toLocaleString()}`);
  }
  if (meta.length) {
    lines.push(...meta, '', '---', '');
  }
  lines.push(item.body || '_No content._', '');
  return lines.join('\n');
}

function safeFilename(base) {
  const stem = (base || 'item').replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 40) || 'item';
  return `${stem}.md`;
}

/**
 * ItemDetailModal — the "open to the fore" reader for a captured decision,
 * artefact, or todo. Shows the full content rendered as Markdown, a chip that
 * links to its provenance (the source thread), and a one-click download of the
 * content as a `.md` file. Closes on the ✕, backdrop click, or Escape.
 *
 * Editable items (artefacts, flagged with `item.editable`) also get a pencil in
 * the header that swaps the reader for a title + body editor, saved through
 * `onSave(item, { title, content })` — the caller owns the persistence path.
 */
export default function ItemDetailModal({ item, onClose, onJoinThread, onSave }) {
  const canEdit = !!item?.editable && typeof onSave === 'function';
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // A different item in the lightbox always starts in read mode.
  useEffect(() => {
    setEditing(false);
    setSaving(false);
    setSaveError('');
  }, [item?.id]);

  const startEditing = useCallback(() => {
    setDraftTitle(item?.title || '');
    setDraftBody(item?.body || '');
    setSaveError('');
    setEditing(true);
  }, [item]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setSaveError('');
  }, []);

  const handleSave = useCallback(async () => {
    if (!item || saving) return;
    const title = draftTitle.trim();
    if (!title) {
      setSaveError('Title is required.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await onSave(item, { title, content: draftBody });
      setEditing(false);
    } catch (err) {
      setSaveError(err?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  }, [item, saving, draftTitle, draftBody, onSave]);

  const handleDownload = useCallback(() => {
    if (!item) return;
    const blob = new Blob([buildMarkdown(item)], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeFilename(item.title);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [item]);

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape backs out of the editor first, so an in-progress edit is never
      // thrown away by the same keypress that closes the lightbox.
      if (editing) {
        setEditing(false);
        setSaveError('');
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose, editing]);

  if (!item) return null;

  const dateText = (() => {
    if (!item.at) return '';
    const d = new Date(item.at);
    return Number.isNaN(d.getTime()) ? item.at : d.toLocaleString();
  })();
  const fineprint = [item.by ? `by ${item.by}` : null, dateText].filter(Boolean).join(' · ');

  return (
    <div
      className="item-detail-backdrop"
      onClick={editing ? undefined : onClose}
      role="presentation"
    >
      <div
        className="item-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-label={item.title || 'Item detail'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="item-detail-header">
          <div className="item-detail-titlewrap">
            {item.tagLabel && (
              <span className={`item-detail-tag ${item.tagClass || ''}`}>{item.tagLabel}</span>
            )}
            {editing ? (
              <input
                type="text"
                className="item-detail-title-input"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Title"
                aria-label="Artefact title"
                autoFocus
              />
            ) : (
              <h2 className="item-detail-title">{item.title || 'Untitled'}</h2>
            )}
          </div>
          {canEdit && !editing && (
            <button
              type="button"
              className="item-detail-edit"
              onClick={startEditing}
              aria-label="Edit artefact"
              title="Edit"
            >
              <IconEdit size={16} />
            </button>
          )}
          <button type="button" className="item-detail-close" onClick={onClose} aria-label="Close">
            <IconClose size={18} />
          </button>
        </div>

        <div className="item-detail-meta">
          {fineprint && <span className="item-detail-fineprint">{fineprint}</span>}
          {item.roomId && onJoinThread && (
            <button
              type="button"
              className="item-detail-provenance"
              onClick={() => onJoinThread(item.roomId)}
              title={`Open “${item.roomName}”`}
            >
              <IconMessage size={13} />
              <span>{item.roomName || 'Open thread'}</span>
            </button>
          )}
        </div>

        <div className="item-detail-body">
          {editing ? (
            <textarea
              className="item-detail-body-input"
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              placeholder="Content (Markdown)"
              aria-label="Artefact content"
            />
          ) : item.body ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.body}</ReactMarkdown>
          ) : (
            <p className="item-detail-empty">No content.</p>
          )}
        </div>

        {saveError && <p className="item-detail-error">{saveError}</p>}

        <div className="item-detail-actions">
          {editing ? (
            <>
              <button
                type="button"
                className="item-detail-cancel"
                onClick={cancelEditing}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="item-detail-save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <button type="button" className="item-detail-download" onClick={handleDownload}>
              <IconMarkdown size={15} />
              <span>Download .md</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
